import siteService from "./siteService";
import { useViewerStore } from "../store/store";
import { DesignData } from "../@types/types";
import { pickCustomerSiteRestrictions } from "../helpers/customerSiteRestrictions";
import { applySunPositionVersionFromDesign } from "../helpers/sunPositionVersion";

/**
 * API often omits or lags `Customer_Site_Restrictions` on GET; keep in-store / cached envelope
 * in sync so finalize + design-data refetch do not wipe notes the user still has locally.
 */
function mergeCustomerSiteRestrictionsFromRetrieve(
  response: any[],
  pickedFromApi: string,
): { next: string; siteDesignDetails: any[] } {
  const prev = (useViewerStore.getState().customerSiteRestrictions ?? "").trim();
  const next = pickedFromApi.trim() !== "" ? pickedFromApi : prev;

  if (!Array.isArray(response) || response.length === 0) {
    return { next, siteDesignDetails: response };
  }

  if (next.trim() === "") {
    return { next, siteDesignDetails: response };
  }

  const siteRow = response[0];
  return {
    next,
    siteDesignDetails: [
      {
        ...siteRow,
        Customer_Site_Restrictions: next,
        customer_Site_Restrictions: next,
      },
      ...response.slice(1),
    ],
  };
}

export async function getDesignVersionList(salesforceId: string): Promise<DesignData[] | null> {
  const response = await siteService.retrieveSiteDesignDetails(salesforceId);
  if (response?.Message === "No records found") {
    applySunPositionVersionFromDesign(null);
    useViewerStore.setState({
      siteDesignDetails: response,
      rsaNotesFromDesign: "",
      rsaStatusFromDesign: undefined,
      customerSiteRestrictions: ""
    });
    return null;
  }

  applySunPositionVersionFromDesign(response[0].Design?.[0]);

  if(response[0].NonFinalizedDesign.length > 0){
    useViewerStore.setState({
      isNonFinalizedSavedDesign: true,
      nonFinalizedDesignData: response[0].NonFinalizedDesign[0].Design[0],
      rsaNotesFromDesign: response[0].RSA_Notes?.slice(0, 255) ?? "",
      rsaStatusFromDesign: response[0].RSA_Status,
    });
    const nfDesign = response[0].NonFinalizedDesign[0].Design[0];
    const picked = pickCustomerSiteRestrictions(nfDesign, response[0]);
    const { next, siteDesignDetails } = mergeCustomerSiteRestrictionsFromRetrieve(response, picked);
    useViewerStore.setState({
      isNonFinalizedSavedDesign: true,
      nonFinalizedDesignData: nfDesign,
      siteDesignDetails,
      customerSiteRestrictions: next,
    });
    return null;
  }

  const picked = pickCustomerSiteRestrictions(response[0].Design[0], response[0]);
  const { next, siteDesignDetails } = mergeCustomerSiteRestrictionsFromRetrieve(response, picked);

  useViewerStore.setState({
    selectedLossesVendor: response[0].Design[0].MultipliersVendor,
    efficiencyOverride: response[0].Design[0].TsrfEfficiencyOverride,
    SFannualUsage: response[0].Design[0].Buildings[0].Max_System_Size_Electric_Load,
    siteBuildingData: response[0].Design[0].Buildings,
    siteDesignDetails,
    rsaNotesFromDesign: response[0].RSA_Notes?.slice(0, 255) ?? "",
    customerSiteRestrictions: next,
    rsaStatusFromDesign: response[0].RSA_Status,
  });

  return assembleDesignVersionList(response);
}

/**
 * Same order as the original Design & Versioning drawer:
 * active designs first, then inactive sorted by design number and version descending.
 */
export function assembleDesignVersionList(response: unknown): DesignData[] {
  const row = Array.isArray(response) ? response[0] : response;
  if (!row || typeof row !== 'object') return [];
  const record = row as {
    Message?: string;
    Design?: DesignData[];
    InactiveDesign?: DesignData[];
    NonFinalizedDesign?: unknown[];
  };
  if (record.Message === 'No records found') return [];
  if (Array.isArray(record.NonFinalizedDesign) && record.NonFinalizedDesign.length > 0) {
    return [];
  }
  const activeDesign = Array.isArray(record.Design) ? [...record.Design] : [];
  const inactiveDesign = Array.isArray(record.InactiveDesign) ? [...record.InactiveDesign] : [];
  inactiveDesign.sort((a: DesignData, b: DesignData) => {
    if (a.Design_No > b.Design_No) return -1;
    if (a.Design_No < b.Design_No) return 1;
    return b.Version_No - a.Version_No;
  });
  return [...activeDesign, ...inactiveDesign];
}

/** Newest finalized design first (modified/created, then design no / version). */
export function sortDesignsLastFinalizedFirst(designs: DesignData[]): DesignData[] {
  return [...designs].sort((a, b) => {
    const tb = Date.parse(String(b.User_Modified_Timestamp || b.User_Created_Timestamp || '')) || 0;
    const ta = Date.parse(String(a.User_Modified_Timestamp || a.User_Created_Timestamp || '')) || 0;
    if (tb !== ta) return tb - ta;
    const designDelta = Number(b.Design_No) - Number(a.Design_No);
    if (designDelta !== 0) return designDelta;
    return Number(b.Version_No) - Number(a.Version_No);
  });
}

/**
 * Finalized oneDRAW versions for PCI All Designs.
 * Still returns active + inactive rows when a leftover NonFinalizedDesign record is present,
 * otherwise the new save never appears and only Max Layout cards stay visible.
 */
export async function fetchFinalizedDesignVersions(salesforceId: string): Promise<DesignData[]> {
  const response = await siteService.retrieveSiteDesignDetails(salesforceId);
  const row = Array.isArray(response) ? response[0] : response;
  if (!row || typeof row !== 'object') return [];
  const record = row as {
    Message?: string;
    Design?: DesignData[];
    InactiveDesign?: DesignData[];
  };
  if (record.Message === 'No records found') return [];
  const activeDesign = Array.isArray(record.Design) ? [...record.Design] : [];
  const inactiveDesign = Array.isArray(record.InactiveDesign) ? [...record.InactiveDesign] : [];
  return sortDesignsLastFinalizedFirst([...activeDesign, ...inactiveDesign]);
}
